import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

/* Match main layout offset (fixed header + banner ~158px) so hero + opening strip fit one viewport */
const NAV_LAYOUT_OFFSET = '158px';

const HeroContainer = styled.div`
  position: relative;
  width: 100%;
  ${props =>
    props.$compact
      ? `
    height: min(440px, calc(58vh - ${NAV_LAYOUT_OFFSET}));
    min-height: 300px;
    max-height: 500px;
  `
      : `
    height: calc(100vh - ${NAV_LAYOUT_OFFSET});
    height: calc(100dvh - ${NAV_LAYOUT_OFFSET});
    min-height: 320px;
  `}
  overflow-x: hidden;
  overflow-y: visible;
  background-color: #020203;
  z-index: 0;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: ${props =>
      props.$compact && props.$centered
        ? /* Symmetric vignette — avoids diagonal bands behind centred copy */
          `radial-gradient(
            ellipse 95% 85% at 50% 48%,
            rgba(8, 14, 38, 0.45) 0%,
            rgba(8, 14, 38, 0.22) 48%,
            rgba(8, 14, 38, 0.06) 72%,
            transparent 88%
          ),
          linear-gradient(
            180deg,
            rgba(5, 10, 28, 0.42) 0%,
            transparent 58%
          )`
        : props.$compact
          ? `linear-gradient(
            160deg,
            rgba(10, 18, 44, 0.72) 0%,
            rgba(10, 18, 44, 0.48) 38%,
            rgba(10, 18, 44, 0.22) 68%,
            transparent 100%
          ),
          linear-gradient(
            0deg,
            rgba(6, 12, 30, 0.55) 0%,
            transparent 42%
          )`
          : props.$overlay === 'bright'
            ? `linear-gradient(180deg, rgba(2, 2, 3, 0.06) 0%, rgba(2, 2, 3, 0.16) 100%)`
            : props.$overlay === 'light'
              ? `radial-gradient(circle at 20% 20%, rgba(10, 10, 16, 0.12), transparent 45%),
           linear-gradient(180deg, rgba(2, 2, 3, 0.22) 0%, rgba(2, 2, 3, 0.42) 100%)`
              : `radial-gradient(circle at 20% 20%, rgba(10, 10, 16, 0.3), transparent 45%),
           linear-gradient(180deg, rgba(2, 2, 3, 0.6) 0%, rgba(2, 2, 3, 0.85) 100%)`};
    opacity: ${props => (props.$overlay === 'none' ? 0 : 1)};
    transition: opacity 0.4s ease;
    pointer-events: none;
    z-index: -1;
  }

  @media (max-width: 768px) {
    height: auto;
    min-height: auto;
    display: flex;
    flex-direction: column;
  }
`;

const VideoBackground = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  z-index: -2;

  @media (max-width: 768px) {
    position: relative;
    inset: auto;
    height: ${props => (props.$compact ? '38vh' : '50vh')};
    min-height: ${props => (props.$compact ? '220px' : '50vh')};
    pointer-events: none;
    z-index: 0;
  }
`;

const imageBackgroundBase = `
  background-size: cover;
`;

const ImageBackground = styled.div`
  position: absolute;
  inset: 0;
  background-image: url("${props => props.$src}");
  background-position: ${props => props.$position || 'center center'};
  z-index: -2;
  transform: scale(1.02);
  transition: transform 8s ease;
  ${imageBackgroundBase}

  @media (max-width: 768px) {
    position: relative;
    inset: auto;
    height: ${props => (props.$compact ? '38vh' : '50vh')};
    min-height: ${props => (props.$compact ? '220px' : '50vh')};
    transform: none;
    z-index: 0;
  }
`;

const FadeImageLayer = styled.div`
  position: absolute;
  inset: 0;
  background-image: url("${props => props.$src}");
  z-index: -2;
  opacity: ${props => (props.$active ? 1 : 0)};
  transition: opacity 1.8s ease-in-out;
  ${imageBackgroundBase}

  @media (max-width: 768px) {
    position: absolute;
    inset: 0;
    height: 100%;
    min-height: 0;
    z-index: 0;
  }
`;

const RotatingImageStack = styled.div`
  position: absolute;
  inset: 0;
  z-index: -2;

  @media (max-width: 768px) {
    position: relative;
    inset: auto;
    height: ${props => (props.$compact ? '38vh' : '50vh')};
    min-height: ${props => (props.$compact ? '220px' : '50vh')};
    z-index: 0;
  }
`;

const ImageVideoStack = styled.div`
  position: absolute;
  inset: 0;
  z-index: -2;

  @media (max-width: 768px) {
    position: relative;
    inset: auto;
    height: ${props => (props.$compact ? '38vh' : '50vh')};
    min-height: ${props => (props.$compact ? '220px' : '50vh')};
    z-index: 0;
  }
`;

const StackImage = styled.div`
  position: absolute;
  inset: 0;
  background-image: url("${props => props.$src}");
  ${imageBackgroundBase}
`;

const StackVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  opacity: ${props => (props.$visible ? 1 : 0)};
  transition: opacity 1.2s ease-in-out;
`;

/**
 * Shows a still image immediately, then after `delay` ms starts loading the
 * video and fades it in over the image once it's actually playing.
 */
const ImageThenVideoBackground = ({ image, videoSrc, compact, delay = 1000 }) => {
  const videoRef = React.useRef(null);
  const [started, setStarted] = React.useState(false);
  const [playing, setPlaying] = React.useState(false);

  React.useEffect(() => {
    const timer = window.setTimeout(() => setStarted(true), delay);
    return () => window.clearTimeout(timer);
  }, [delay]);

  React.useEffect(() => {
    if (!started) return;
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    video.src = videoSrc;
    video.load();
    const playPromise = video.play();
    if (playPromise?.catch) playPromise.catch(() => {});
  }, [started, videoSrc]);

  return (
    <ImageVideoStack $compact={compact} aria-hidden>
      <StackImage $src={image} role="presentation" />
      {started && (
        <StackVideo
          ref={videoRef}
          $visible={playing}
          muted
          loop
          playsInline
          onPlaying={() => setPlaying(true)}
        />
      )}
    </ImageVideoStack>
  );
};

const RotatingImageBackground = ({ images, compact, interval = 6000 }) => {
  const [activeIndex, setActiveIndex] = React.useState(0);
  const prefersReducedMotion = React.useMemo(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    []
  );

  React.useEffect(() => {
    if (images.length <= 1 || prefersReducedMotion) return undefined;

    const timer = window.setInterval(() => {
      setActiveIndex(prev => (prev + 1) % images.length);
    }, interval);

    return () => window.clearInterval(timer);
  }, [images.length, interval, prefersReducedMotion]);

  return (
    <RotatingImageStack $compact={compact} aria-hidden>
      {images.map((src, index) => (
        <FadeImageLayer
          key={src}
          $src={src}
          $active={index === activeIndex}
          role="presentation"
        />
      ))}
    </RotatingImageStack>
  );
};

const LazyVideo = ({ src, compact }) => {
  const videoRef = React.useRef(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && videoRef.current) {
          videoRef.current.src = src;
          videoRef.current.load();
          observer.unobserve(entry.target);
        }
      });
    }, options);

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => {
      if (videoRef.current) {
        observer.unobserve(videoRef.current);
      }
    };
  }, [src]);

  return (
    <VideoBackground
      ref={videoRef}
      $compact={compact}
      autoPlay
      muted
      loop
      playsInline
      onLoadedData={() => setIsLoading(false)}
    />
  );
};

const HeroContent = styled.div`
  position: absolute;
  top: 50%;
  left: ${props => (props.$centered ? '50%' : 'clamp(1.25rem, 7vw, 5rem)')};
  transform: ${props =>
    props.$centered ? 'translate(-50%, -50%)' : 'translateY(-50%)'};
  max-width: ${props =>
    props.$centered ? 'min(40rem, 92vw)' : 'min(44rem, 88vw)'};
  text-align: ${props => (props.$centered ? 'center' : 'left')};
  color: #fff;
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    pointer-events: none;
    z-index: -1;
    ${props =>
      props.$centered
        ? `
      /* No extra scrim — full-hero vignette handles contrast; avoids uneven “shadow” edges */
      display: none;
    `
        : props.$overlay === 'bright'
          ? `
      left: -18%;
      transform: translateY(-50%);
      width: min(145%, 58rem);
      height: 155%;
      max-height: 30rem;
      background-image:
        radial-gradient(
          ellipse 95% 115% at 12% 48%,
          rgba(5, 12, 30, 0.1) 0%,
          rgba(5, 12, 30, 0.03) 52%,
          transparent 72%
        );
    `
          : props.$overlay === 'light'
            ? `
      left: -18%;
      transform: translateY(-50%);
      width: min(145%, 58rem);
      height: 155%;
      max-height: 30rem;
      background-image:
        radial-gradient(
          ellipse 95% 115% at 12% 48%,
          rgba(5, 12, 30, 0.18) 0%,
          rgba(5, 12, 30, 0.06) 52%,
          transparent 72%
        ),
        linear-gradient(
          102deg,
          rgba(3, 10, 26, 0.34) 0%,
          rgba(3, 10, 26, 0.18) 22%,
          rgba(3, 10, 26, 0.08) 42%,
          rgba(3, 10, 26, 0.03) 58%,
          transparent 78%
        );
    `
          : `
      left: -18%;
      transform: translateY(-50%);
      width: min(145%, 58rem);
      height: 155%;
      max-height: 30rem;
      background-image:
        radial-gradient(
          ellipse 95% 115% at 12% 48%,
          rgba(5, 12, 30, 0.33) 0%,
          rgba(5, 12, 30, 0.12) 52%,
          transparent 72%
        ),
        linear-gradient(
          102deg,
          rgba(3, 10, 26, 0.62) 0%,
          rgba(3, 10, 26, 0.38) 22%,
          rgba(3, 10, 26, 0.2) 42%,
          rgba(3, 10, 26, 0.07) 58%,
          transparent 78%
        );
    `}
  }

  @media (prefers-reduced-transparency: reduce) {
    &::before {
      ${props =>
        props.$centered
          ? `
        display: none;
      `
          : `
        background-image: linear-gradient(
          102deg,
          rgba(3, 10, 26, 0.75) 0%,
          rgba(3, 10, 26, 0.45) 35%,
          rgba(3, 10, 26, 0.2) 58%,
          transparent 85%
        );
      `}
    }
  }

  @media (max-width: 768px) {
    position: relative;
    top: 0;
    left: 0;
    transform: none;
    max-width: none;
    width: 100%;
    text-align: center;
    padding: 2.25rem 1.35rem 2.5rem;
    margin: 0 0 1.25rem;
    box-sizing: border-box;
    background: linear-gradient(
      165deg,
      ${theme.colors.primary} 0%,
      #0a1430 100%
    );
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);

    &::before {
      display: none;
    }
  }
`;

const HeroDivider = styled.span`
  display: block;
  width: 2.75rem;
  height: 1px;
  margin: 0 0 1.1rem;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.55), transparent);

  ${props =>
    props.$centered &&
    `
    margin-left: auto;
    margin-right: auto;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.52),
      transparent
    );
  `}

  @media (max-width: 768px) {
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 1rem;
    width: 2.25rem;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.45),
      transparent
    );
  }
`;

const WelcomeText = styled.h2`
  font-family: ${theme.fonts.main};
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.26em;
  text-transform: uppercase;
  margin: 0 0 0.85rem;
  color: rgba(255, 255, 255, 0.78);

  @media (max-width: 768px) {
    font-size: 0.7rem;
    letter-spacing: 0.22em;
    margin-bottom: 0.65rem;
    color: rgba(255, 255, 255, 0.72);
  }
`;

const CastleTitle = styled.h1`
  font-family: ${theme.fonts.main};
  font-size: ${props =>
    props.$compact ? 'clamp(2.2rem, 5vw, 3.5rem)' : 'clamp(2.85rem, 7.5vw, 6rem)'};
  font-weight: 700;
  line-height: 1.04;
  letter-spacing: -0.02em;
  margin: 0 0 ${props => (props.$compact ? '0.85rem' : '1.15rem')};
  color: #fff;
  text-shadow: 0 2px 32px rgba(0, 0, 0, 0.38);

  @media (max-width: 768px) {
    font-size: clamp(2.1rem, 9vw, 3rem);
    margin-bottom: 0.85rem;
    line-height: 1.05;
  }
`;

const Subtitle = styled.h3`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: clamp(0.69rem, 1.2vw, 0.8125rem);
  font-weight: 500;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  line-height: 1.7;
  margin: 0 0 2rem;
  max-width: 36rem;
  color: rgba(255, 255, 255, 0.82);
  text-shadow: 0 1px 28px rgba(0, 0, 0, 0.35);

  ${props =>
    props.$centered &&
    `
    margin-left: auto;
    margin-right: auto;
  `}

  @media (max-width: 768px) {
    font-size: 0.65rem;
    letter-spacing: 0.26em;
    margin: 0 auto 1.75rem;
    line-height: 1.65;
    max-width: 22rem;
    color: rgba(255, 255, 255, 0.76);
  }
`;

const InfoSection = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: stretch;
  gap: 0;
  margin: 0;
  padding: 1.25rem clamp(1rem, 4vw, 2.75rem);
  padding-bottom: calc(1.25rem + env(safe-area-inset-bottom, 0px));
  color: #fff;
  border-radius: 14px 14px 0 0;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-bottom: none;
  box-shadow: 0 -12px 48px rgba(0, 0, 0, 0.35);
  background: rgba(16, 29, 64, 0.82);
  backdrop-filter: blur(18px) saturate(165%);
  -webkit-backdrop-filter: blur(18px) saturate(165%);

  @supports (backdrop-filter: blur(12px)) {
    background: rgba(16, 29, 64, 0.58);
  }

  @media (prefers-reduced-transparency: reduce) {
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    background: ${theme.colors.primary};
  }

  @media (max-width: 1024px) {
    padding: 1.1rem clamp(0.75rem, 3vw, 1.5rem);
    padding-bottom: calc(1.1rem + env(safe-area-inset-bottom, 0px));
  }

  @media (max-width: 768px) {
    position: relative;
    grid-template-columns: 1fr;
    border-radius: 12px;
    border: 1px solid rgba(16, 29, 64, 0.1);
    margin: 0 auto 2rem;
    width: calc(100% - 2rem);
    max-width: 420px;
    padding: 0;
    padding-bottom: 0;
    box-shadow: 0 12px 40px rgba(16, 29, 64, 0.1);
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    background: ${theme.colors.offWhite};
    color: ${theme.colors.primary};
  }
`;

const InfoColumn = styled.div`
  padding: 0.5rem clamp(0.75rem, 2vw, 1.5rem);
  text-align: center;

  &:not(:last-child) {
    border-right: 1px solid rgba(255, 255, 255, 0.14);
  }

  @media (max-width: 768px) {
    text-align: left;
    padding: 1.15rem 1.35rem;

    &:not(:last-child) {
      border-right: none;
      border-bottom: 1px solid rgba(16, 29, 64, 0.1);
    }

    &:first-child {
      padding-top: 1.35rem;
    }

    &:last-child {
      padding-bottom: 1.35rem;
    }
  }
`;

const InfoTitle = styled.h4`
  font-family: ${theme.fonts.main};
  font-size: 0.72rem;
  font-weight: 600;
  margin: 0 0 0.55rem;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: rgba(255, 255, 255, 0.72);

  @media (max-width: 768px) {
    font-size: 0.68rem;
    margin: 0 0 0.45rem;
    letter-spacing: 0.12em;
    color: rgba(16, 29, 64, 0.65);
  }
`;

const InfoText = styled.p`
  font-size: 0.98rem;
  font-weight: 500;
  margin: 0.2rem 0;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.96);

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin: 0.15rem 0;
    color: ${theme.colors.primary};
    font-weight: 500;
  }
`;

const InfoLink = styled.a`
  font-size: 0.98rem;
  font-weight: 500;
  margin: 0.2rem 0;
  line-height: 1.55;
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  display: block;
  transition: color 0.2s ease, transform 0.2s ease;
  position: relative;
  padding: 2px 0;

  &:hover {
    color: rgba(255, 255, 255, 1);
    transform: translateX(2px);
  }

  &::after {
    content: '→';
    margin-left: 6px;
    opacity: 0;
    transition: opacity 0.2s ease, transform 0.2s ease;
    transform: translateX(-4px);
    display: inline-block;
  }

  &:hover::after {
    opacity: 1;
    transform: translateX(0);
  }

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin: 0.15rem 0;
    color: ${theme.colors.primary};

    &:hover {
      color: ${theme.colors.accent};
      transform: none;
    }
  }
`;

const InfoNote = styled.p`
  font-size: 0.78rem;
  margin: 0.65rem 0 0;
  line-height: 1.45;
  font-style: italic;
  color: rgba(255, 255, 255, 0.62);

  @media (max-width: 768px) {
    font-size: 0.78rem;
    margin: 0.5rem 0 0;
    color: rgba(16, 29, 64, 0.58);
  }
`;

const HeroButton = styled.button`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  margin: 0;
  padding: 0.95rem 1.65rem;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.96);
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 999px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: background 0.25s ease, border-color 0.25s ease, color 0.25s ease,
    transform 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.95);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    font-size: 0.68rem;
    padding: 0.85rem 1.35rem;
    letter-spacing: 0.14em;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    background: transparent;
    border-color: rgba(255, 255, 255, 0.85);

    &:hover {
      background: rgba(255, 255, 255, 0.14);
      transform: none;
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem 0.85rem;

  ${props =>
    props.$centered &&
    `
    justify-content: center;
  `}

  @media (max-width: 768px) {
    justify-content: center;
    gap: 0.65rem;
    margin: 0;

    ${HeroButton} {
      min-width: 148px;

      &:hover {
        background: rgba(255, 255, 255, 0.95);
        color: ${theme.colors.primary};
        border-color: #fff;
      }
    }
  }
`;

const Hero = ({ 
  videoSrc,
  backgroundImage,
  backgroundImages,
  backgroundPosition,
  introImage,
  introVideoSrc,
  introVideoDelay = 1000,
  welcomeText,
  mainTitle,
  subtitle,
  infoColumns = [],
  buttons = [],
  darkOverlay = false,
  brightOverlay = false,
  compact = false,
  centered = false
}) => {
  const hasIntroVideo = Boolean(introImage && introVideoSrc);
  const hasRotatingImages = Array.isArray(backgroundImages) && backgroundImages.length > 0;
  const overlay = darkOverlay
    ? 'dark'
    : brightOverlay
      ? 'bright'
      : backgroundImage || hasRotatingImages || hasIntroVideo
        ? 'light'
        : 'none';
  return (
    <HeroContainer $overlay={overlay} $compact={compact} $centered={centered}>
      {hasIntroVideo ? (
        <ImageThenVideoBackground
          image={introImage}
          videoSrc={introVideoSrc}
          compact={compact}
          delay={introVideoDelay}
        />
      ) : hasRotatingImages ? (
        <RotatingImageBackground images={backgroundImages} compact={compact} />
      ) : backgroundImage ? (
        <ImageBackground
          $src={backgroundImage}
          $compact={compact}
          $position={backgroundPosition}
          role="presentation"
        />
      ) : (
        <LazyVideo src={videoSrc} compact={compact} />
      )}
      <HeroContent $centered={centered} $overlay={overlay}>
        {welcomeText ? (
          <>
            <WelcomeText>{welcomeText}</WelcomeText>
            <HeroDivider aria-hidden $centered={centered} />
          </>
        ) : null}
        <CastleTitle $compact={compact}>{mainTitle}</CastleTitle>
        {subtitle ? <Subtitle $centered={centered}>{subtitle}</Subtitle> : null}
        {buttons.length > 0 && (
          <ButtonContainer $centered={centered}>
            {buttons.map((button, index) => (
              <HeroButton key={index} onClick={button.onClick}>
                {button.text}
              </HeroButton>
            ))}
          </ButtonContainer>
        )}
      </HeroContent>

      {infoColumns.length > 0 && (
        <InfoSection>
          {infoColumns.map((column, index) => (
            <InfoColumn key={index}>
              <InfoTitle>{column.title}</InfoTitle>
              {column.items.map((item, itemIndex) => {
                // Support both string items and object items with link
                if (typeof item === 'object' && item.link) {
                  return (
                    <InfoLink 
                      key={itemIndex} 
                      href={item.link}
                      onClick={(e) => {
                        if (item.link.startsWith('/')) {
                          e.preventDefault();
                          window.location.assign(item.link);
                        }
                      }}
                    >
                      {item.text}
                    </InfoLink>
                  );
                }
                return <InfoText key={itemIndex}>{typeof item === 'string' ? item : item.text}</InfoText>;
              })}
              {column.note && <InfoNote>{column.note}</InfoNote>}
            </InfoColumn>
          ))}
        </InfoSection>
      )}
    </HeroContainer>
  );
};

export default Hero;
